/**
 * src/components/shared/UpdateBanner.jsx — "New version available — Reload" banner.
 *
 * TICKET-089. Replaces main.jsx's silent auto-reload-on-controllerchange (which
 * could yank the CEO mid-action) with a visible, user-controlled prompt. Shows a
 * top banner when a newer deployed build is ready (see lib/pwa/swUpdate.js), and
 * reloads on tap so the new bundle takes over only when the CEO chooses.
 *
 * Mirrors ProviderStatusBanner's container conventions: fixed top, clears the
 * Dynamic Island via paddingTop calc(8px + env(safe-area-inset-top)), tokens only.
 *
 * AEGIS P2-02: UpdateBanner hides when offline — a new deploy while offline is
 * unreachable and the OfflineBanner (z-50) takes priority for user attention.
 * UpdateBanner will reappear once the device reconnects (navigator.onLine).
 *
 * i18n keys: update.available / update.reload / update.dismiss (en + es).
 */
import { useEffect, useState, useCallback } from 'react';
import { RotateCw, X, Sparkles } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { watchForSwUpdate, applySwUpdate } from '../../lib/pwa/swUpdate';
import { markReloadRestore } from '../../lib/nav/reloadRestore';

export default function UpdateBanner() {
  const { t } = useT();
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // AEGIS P2-02: mirror OfflineBanner's online/offline signal so we don't
  // stack on top of it when both are active simultaneously.
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const stop = watchForSwUpdate(() => setAvailable(true));
    return stop;
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const onReload = useCallback(() => {
    // Preserve the user's spot (tab + open project) across the reload so they land
    // back where they were instead of the default Focus view — CEO 2026-06-18.
    markReloadRestore();
    // TICKET-771: activate the waiting worker and start the quiet period, THEN
    // reload — a bare reload left the update pending and the banner came back on
    // every load (CEO, mid-UAT: "I reload over and over and don't get past it").
    applySwUpdate();
  }, []);

  // Hide when offline (OfflineBanner takes priority) or update not yet detected/dismissed
  if (!available || dismissed || !isOnline) return null;

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 60, // above OfflineBanner/ProviderStatusBanner (50), below modals (1000+)
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '8px 16px',
    paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--on-navy)',
    background: 'var(--navy-surface)',
    flexWrap: 'wrap',
  };

  const reloadBtnStyle = {
    minHeight: 44,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--accent)',
    color: 'var(--card)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const dismissBtnStyle = {
    minHeight: 44,
    minWidth: 44,
    padding: 8,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.15)',
    color: 'var(--on-navy)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div data-testid="update-banner" style={containerStyle} role="status" aria-live="polite">
      <Sparkles size={16} aria-hidden="true" />
      <span>{t('update.available')}</span>
      <button type="button" onClick={onReload} style={reloadBtnStyle} data-testid="update-banner-reload">
        <RotateCw size={14} />
        {t('update.reload')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('update.dismiss')}
        style={dismissBtnStyle}
        data-testid="update-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
